import { safeReturnToPath } from "../../utils/auth_navigation";
import { effectiveAccessTier } from "../../utils/user";
import { fetchWorkspaceChartBarsWithRetry } from "./workspaceChartHydration";

const DEFAULT_UPGRADE_ROUTE = "/plan-selection";
const PLANS_INCLUDING_TIER = {
  pro: "Pro and Max",
  max: "Max",
};
const TIER_LABELS = {
  pro: "Pro",
  max: "Max",
};

// Bars withheld by the account's access are not a failed load. The server's
// chartAvailability owns that decision; this only maps it to a chart state.
export const workspaceChartAccessBlock = (availability) => {
  if (!availability || availability.eligible !== false) return null;

  if (availability.status === "paid_plan_required") {
    return {
      status: availability.status,
      assetType: availability.assetType || null,
      requiredTier: availability.requiredTier || null,
      upgradeRoute:
        safeReturnToPath(availability.upgradeRoute) || DEFAULT_UPGRADE_ROUTE,
      // Only an access change can unlock it; polling cannot.
      recoverable: false,
    };
  }
  if (availability.status === "feature_disabled") {
    return {
      status: availability.status,
      assetType: availability.assetType || null,
      requiredTier: null,
      upgradeRoute: null,
      // Operators can turn the data source back on.
      recoverable: true,
    };
  }
  return null;
};

export const workspaceChartAccessCopy = ({
  block,
  tickerLabel = null,
  hasOtherCharts = false,
}) => {
  if (!block) return null;

  const futures = block.assetType === "futures";
  const otherCharts = hasOtherCharts ? " Choose another chart above." : "";
  if (block.status === "feature_disabled") {
    return {
      title: `${tickerLabel || "This chart"} isn't available right now`,
      body: `${futures ? "Futures data" : "This market's data"} is temporarily unavailable. The chart keeps its settings and loads when the data returns.${otherCharts}`,
      actionLabel: null,
    };
  }

  const tierLabel = TIER_LABELS[block.requiredTier] || "a paid plan";
  const plans = PLANS_INCLUDING_TIER[block.requiredTier] || "paid plans";
  return {
    title: tickerLabel
      ? `${tickerLabel} charts need ${tierLabel}`
      : `This chart needs ${tierLabel}`,
    body: `${futures ? "Futures charts are" : "This chart is"} included with ${plans}. The chart keeps its settings and loads once your plan includes it.${otherCharts}`,
    actionLabel: "See plans",
  };
};

// Changes when the account's product access can change what a chart may
// load: sign-in, upgrade or downgrade, or an admin test tier.
export const workspaceChartAccountAccessKey = (userData) =>
  `${userData?.user_id ?? "visitor"}:${effectiveAccessTier(userData)}`;

// Hydrates a series. When a response is unusable, asks once whether access
// withholds it, and stops retrying a request that cannot succeed.
export const fetchWorkspaceChartBarsOrAccessBlock = async ({
  fetchBars,
  fetchAvailability,
  shouldContinue = () => true,
  ...retryOptions
}) => {
  let block = null;
  let accessRequest = null;
  const accessBlock = () => {
    accessRequest ||= Promise.resolve()
      .then(fetchAvailability)
      .then(workspaceChartAccessBlock)
      .catch(() => null);
    return accessRequest;
  };

  let bars = [];
  let failure = null;
  try {
    bars = await fetchWorkspaceChartBarsWithRetry({
      ...retryOptions,
      fetchBars: async () => {
        const rows = await fetchBars();
        if (!Array.isArray(rows) || rows.length < 2) block = await accessBlock();
        return rows;
      },
      shouldContinue: () => !block && shouldContinue(),
    });
  } catch (error) {
    failure = error;
  }

  if (bars.length >= 2) return { bars, block: null };
  if (!block && shouldContinue()) block = await accessBlock();
  if (block) return { bars: [], block };
  if (failure) throw failure;
  return { bars, block: null };
};
